import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Upload, X, ImagePlus, Loader2, Rocket, Truck, MapPin, Star } from "lucide-react";
import { Reorder } from "framer-motion";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES, ISLANDS, ISLAND_NAME, CONDITIONS, BOOST_PACKAGES } from "../lib/data";
import { formatPrice } from "../lib/format";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import api, { fileUrl, formatApiError } from "../lib/api";
import { compressImage } from "../lib/imageCompress";
import { toast } from "sonner";

export default function Sell() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get("edit");
  const fileRef = useRef();

  const [form, setForm] = useState({
    title: "", description: "", category: "", price: "", condition: "usado",
    island: user?.island || "", municipality: user?.municipality || "", zone: "",
    delivery: "ambos", tracking_number: "",
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [saving, setSaving] = useState(false);
  const [boostDays, setBoostDays] = useState(0);   // 0 = no boost
  const [payOpen, setPayOpen] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const munis = ISLANDS.find((i) => i.id === form.island)?.municipalities || [];
  const selectedPkg = BOOST_PACKAGES.find((p) => p.days === boostDays);

  useEffect(() => {
    if (user === false) navigate("/auth");
  }, [user, navigate]);

  useEffect(() => {
    if (!editId && user && user.island) {
      setForm((p) => (p.island ? p : { ...p, island: user.island, municipality: user.municipality || "" }));
    }
  }, [user, editId]);

  useEffect(() => {
    if (editId) {
      api.get(`/listings/${editId}`).then(({ data }) => {
        setForm({ title: data.title, description: data.description, category: data.category, price: data.price,
          condition: data.condition, island: data.island, municipality: data.municipality, zone: data.zone || "",
          delivery: "ambos",
          tracking_number: data.tracking_number || "" });
        setPhotos(data.photos || []);
      });
    }
  }, [editId]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files).slice(0, 10 - photos.length);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    // Downscale to HD on-device, then upload all in parallel so 10 photos stay fast.
    const optimised = await Promise.all(files.map((f) => compressImage(f)));
    const results = await Promise.all(
      optimised.map(async (file) => {
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setUploadProgress((p) => ({ ...p, done: p.done + 1 }));
          return data.path;
        } catch (err) {
          toast.error("Error subiendo imagen");
          return null;
        }
      })
    );
    setPhotos((p) => [...p, ...results.filter(Boolean)]);
    setUploading(false);
    setUploadProgress({ done: 0, total: 0 });
  };

  const buildPayload = () => {
    return {
      ...form, price: Number(form.price), photos, delivery: "ambos",
      tracking_number: (form.tracking_number || "").trim() || null,
    };
  };

  const validate = () => {
    if (!form.title || !form.category || !form.price || !form.island || !form.municipality) {
      toast.error(t("common.required")); return false;
    }
    return true;
  };

  const doPublish = async (applyBoost) => {
    setSaving(true);
    const payload = buildPayload();
    try {
      if (editId) {
        await api.put(`/listings/${editId}`, payload);
        toast.success(t("common.saved"));
        navigate("/dashboard");
      } else {
        const { data } = await api.post("/listings", payload);
        if (applyBoost && boostDays) {
          const { data: b } = await api.post(`/listings/${data.id}/boost`, { days: boostDays, origin_url: window.location.origin });
          if (b.checkout_url) { window.location.href = b.checkout_url; return; }
          toast.success(t("sell.boostedPublished"));
        } else {
          toast.success(t("sell.published"));
        }
        navigate("/dashboard");
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiError(e.response?.data?.detail);
      toast.error(detail);
      if (status === 402 || status === 403) { navigate("/planes"); }
    }
    finally { setSaving(false); setPayOpen(false); }
  };

  const submit = async () => {
    if (!validate()) return;
    if (!editId && boostDays) { setPayOpen(true); return; }  // demo payment step
    doPublish(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-6">
      <h1 className="font-display font-extrabold text-2xl sm:text-3xl mb-6">{editId ? t("sell.edit") : t("sell.title")}</h1>

      <div className="space-y-6 bg-white border border-slate-200 rounded-3xl p-5 sm:p-7">
        {/* Photos */}
        <div>
          <Label>{t("sell.photos")}</Label>
          <p className="text-xs text-slate-400 mt-0.5">{t("sell.photosHint")}</p>
          {photos.length > 1 && <p className="text-[11px] text-slate-400 mt-1 mb-3">{t("sell.reorderHint")}</p>}
          {photos.length <= 1 && <div className="mb-3" />}
          <div className="flex items-start gap-3 overflow-x-auto pb-2">
            <Reorder.Group as="div" axis="x" values={photos} onReorder={setPhotos} className="flex gap-3">
              {photos.map((p, i) => (
                <Reorder.Item key={p} value={p} as="div" data-testid={`photo-tile-${i}`}
                  className="relative w-24 h-24 sm:w-28 sm:h-28 shrink-0 rounded-xl overflow-hidden bg-slate-100 cursor-grab active:cursor-grabbing touch-none select-none">
                  <img src={fileUrl(p)} alt="" loading="lazy" draggable={false} className="w-full h-full object-cover pointer-events-none" />
                  {i === 0 ? (
                    <span data-testid="cover-badge" className="absolute bottom-1 left-1 text-[9px] font-mono-badge bg-[#008080] text-white px-1.5 py-0.5 rounded flex items-center gap-0.5"><Star className="h-2.5 w-2.5 fill-white" />{t("sell.cover")}</span>
                  ) : (
                    <button data-testid={`set-cover-${i}`} onPointerDownCapture={(e) => e.stopPropagation()}
                      onClick={() => setPhotos((ps) => { const a = [...ps]; const [m] = a.splice(i, 1); a.unshift(m); return a; })}
                      className="absolute bottom-1 left-1 text-[9px] bg-black/60 hover:bg-[#008080] text-white px-1.5 py-0.5 rounded flex items-center gap-0.5 transition-colors"><Star className="h-2.5 w-2.5" />{t("sell.setCover")}</button>
                  )}
                  <button data-testid={`remove-photo-${i}`} onPointerDownCapture={(e) => e.stopPropagation()}
                    onClick={() => setPhotos((ps) => ps.filter((_, j) => j !== i))}
                    className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"><X className="h-3.5 w-3.5" /></button>
                </Reorder.Item>
              ))}
            </Reorder.Group>
            {uploading && Array.from({ length: Math.max(0, uploadProgress.total - uploadProgress.done) }).map((_, i) => (
              <div key={`ph-${i}`} data-testid="photo-uploading-skeleton" className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 rounded-xl bg-slate-100 grid place-items-center animate-pulse text-slate-400">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ))}
            {!uploading && photos.length < 10 && (
              <button data-testid="add-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 rounded-xl border-2 border-dashed border-slate-300 grid place-items-center text-slate-400 hover:border-teal-400 hover:text-teal-500 transition-colors">
                <ImagePlus className="h-6 w-6" />
              </button>
            )}
          </div>
          {uploading && (
            <p data-testid="upload-progress" className="text-xs text-[#008080] mt-2 flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />{t("sell.uploadingPhotos")} {uploadProgress.done}/{uploadProgress.total}
            </p>
          )}
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} data-testid="photo-input" />
        </div>

        <div><Label>{t("sell.listingTitle")} *</Label>
          <Input data-testid="sell-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={t("sell.titlePh")} className="mt-1" /></div>

        <div><Label>{t("sell.description")}</Label>
          <Textarea data-testid="sell-desc-input" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("sell.descPh")} rows={4} className="mt-1" /></div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>{t("sell.category")} *</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="sell-category-select" className="mt-1"><SelectValue placeholder={t("sell.category")} /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{t(`categories.${c.id}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>{t("sell.price")} *</Label>
            <Input data-testid="sell-price-input" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1" /></div>
        </div>

        <div><Label>{t("sell.condition")}</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {CONDITIONS.map((c) => (
              <button key={c} data-testid={`condition-${c}`} onClick={() => set("condition", c)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.condition === c ? "border-[#008080] bg-teal-50 text-[#008080]" : "border-slate-200"}`}>{t(`conditions.${c}`)}</button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>{t("sell.island")} *</Label>
            <Select value={form.island} onValueChange={(v) => { set("island", v); set("municipality", ""); }}>
              <SelectTrigger data-testid="sell-island-select" className="mt-1"><SelectValue placeholder={t("sell.island")} /></SelectTrigger>
              <SelectContent>{ISLANDS.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>{t("sell.municipality")} *</Label>
            <Select value={form.municipality} onValueChange={(v) => set("municipality", v)} disabled={!form.island}>
              <SelectTrigger data-testid="sell-muni-select" className="mt-1"><SelectValue placeholder={t("sell.municipality")} /></SelectTrigger>
              <SelectContent>{munis.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div><Label>{t("sell.delivery")}</Label>
          <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" />{t("sell.canaryOnly")}</p>
          <div data-testid="sell-delivery-fixed" className="mt-1 space-y-2">
            <div className="flex items-start gap-2 rounded-xl border border-teal-200 bg-teal-50 px-3 py-2.5 text-sm text-teal-800">
              <Truck className="h-4 w-4 text-[#008080] mt-0.5 shrink-0" />
              <span>{t("delivery.envio")} · Correos<br /><span className="text-xs text-teal-700/80">{t("sell.trackedNote")}</span></span>
            </div>
            <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700">
              <MapPin className="h-4 w-4 text-[#008080] mt-0.5 shrink-0" />
              <span>{t("delivery.recogida")}<br /><span className="text-xs text-slate-500">{t("sell.pickupNote")}</span></span>
            </div>
          </div>
        </div>

        {editId && (
          <div data-testid="shipping-section" className="space-y-4 p-4 rounded-2xl bg-slate-50 border border-slate-200 cm-fade-up">
            <div>
              <Label className="flex items-center gap-1.5"><Truck className="h-4 w-4 text-[#008080]" />{t("sell.trackingNumber")}</Label>
              <p className="text-xs text-slate-400 mt-0.5 mb-2">{t("sell.trackingHint")}</p>
              <Input data-testid="sell-tracking-input" value={form.tracking_number || ""}
                onChange={(e) => set("tracking_number", e.target.value)} placeholder="ES0000000000000000000000" />
            </div>
          </div>
        )}

        {!editId && (
          <div data-testid="boost-section" className="p-5 rounded-2xl border-2 border-[#FF9F1C]/30 bg-gradient-to-br from-amber-50/60 to-white">
            <div className="flex items-center gap-2 mb-1">
              <span className="h-8 w-8 grid place-items-center rounded-lg bg-[#FF9F1C] text-white"><Rocket className="h-4 w-4" /></span>
              <h3 className="font-display font-bold text-lg">{t("sell.boostTitle")}</h3>
            </div>
            <p className="text-xs text-slate-500 mb-3">{t("sell.boostLabel")} {formatPrice(1.99, lang)}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <button type="button" data-testid="boost-none" onClick={() => setBoostDays(0)}
                className={`px-3 py-3 rounded-xl border text-sm text-left transition-colors ${boostDays === 0 ? "border-[#008080] bg-teal-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <span className="font-semibold">{t("sell.boostNone")}</span>
                <span className="block text-xs text-slate-400 mt-0.5">{formatPrice(0, lang)}</span>
              </button>
              {BOOST_PACKAGES.map((p) => (
                <button key={p.days} type="button" data-testid={`boost-pkg-${p.days}`} onClick={() => setBoostDays(p.days)}
                  className={`px-3 py-3 rounded-xl border text-sm text-left transition-colors ${boostDays === p.days ? "border-[#FF9F1C] bg-amber-50 ring-1 ring-[#FF9F1C]/40" : "border-slate-200 hover:bg-slate-50"}`}>
                  <span className="font-semibold">{t(`monetization.boosts.${p.key}`)}</span>
                  <span className="block text-xs font-mono-badge text-[#e88f13] mt-0.5">{formatPrice(p.price, lang)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <Button data-testid="publish-btn" onClick={submit} disabled={saving || uploading} className="w-full h-12 bg-[#FF9F1C] hover:bg-[#e88f13] text-white font-bold">
          {saving ? t("sell.publishing") : editId ? t("sell.update")
            : boostDays ? `${t("sell.boostSelected")} · ${formatPrice(selectedPkg?.price || 0, lang)}` : t("sell.publish")}
        </Button>
      </div>

      {/* Demo payment dialog for boost */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-2xl" data-testid="payment-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Rocket className="h-5 w-5 text-[#FF9F1C]" />{t("sell.payTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-amber-50 border border-[#FF9F1C]/30">
              <span className="font-medium">{t("sell.boostTitle")} · {selectedPkg ? t(`monetization.boosts.${selectedPkg.key}`) : ""}</span>
              <span className="font-display font-extrabold text-xl">{formatPrice(selectedPkg?.price || 0, lang)}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
              <span className="font-mono-badge px-2 py-0.5 rounded bg-slate-200 text-slate-600">{t("sell.paySim")}</span>
              <span>{t("sell.paySimNote")}</span>
            </div>
            <Button data-testid="confirm-payment-btn" onClick={() => doPublish(true)} disabled={saving} className="w-full h-11 bg-[#008080] hover:bg-[#006666]">
              {saving ? t("sell.publishing") : t("sell.payConfirm")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
